import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePaymentRequests } from './usePaymentRequests';
import { useExpenses } from './useExpenses';
import { useDepartments } from './useDepartments';
import { useExpenseCategories } from './useExpenseCategories';
import { useExchangeRates } from './useExchangeRates';
import { useAuth } from '@/contexts/AuthContext';
import { format, parseISO, differenceInDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { SupportedCurrency } from '@/lib/currency';

export type DisplayCurrency = SupportedCurrency;

export interface CashOutflowFilters {
  dateFrom?: string;
  dateTo?: string;
  aggregationPeriod: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  displayCurrency?: DisplayCurrency;
}

export interface CashOutflowKPIs {
  totalCashOutflow: number;
  pendingOutflow: number;
  avgProcessingDays: number;
  approvalRate: number;
  monthlyBurnRate: number;
  totalTransactions: number;
  rejectedCount: number;
  paidCount: number;
  internalTransfersExcluded: number;
  internalTransfersCount: number;
}

export interface TimeSeriesDataPoint {
  period: string;
  expenses: number;
  paymentRequests: number;
  total: number;
}

export interface CategoryBreakdown {
  name: string;
  value: number;
  count: number;
}

export interface CostPositionBreakdown {
  id: string;
  name: string;
  code: string;
  value: number;
  count: number;
}

export interface CostGroupBreakdown {
  id: string;
  name: string;
  code: string;
  value: number;
  count: number;
  costPositions: CostPositionBreakdown[];
}

export interface ExpenseCategoryBreakdown {
  id: string;
  name: string;
  code: string;
  value: number;
  count: number;
  costGroups: CostGroupBreakdown[];
}

export interface DepartmentBreakdown {
  name: string;
  value: number;
  count: number;
}

export interface PaymentMethodBreakdown {
  method: string;
  value: number;
  count: number;
}

export interface StatusFunnel {
  status: string;
  count: number;
  value: number;
}

export interface ProcessingTimeData {
  department: string;
  avgDaysToApprove: number;
  avgDaysToPay: number;
  avgTotalDays: number;
}

export interface VendorAnalysis {
  vendorName: string;
  totalAmount: number;
  transactionCount: number;
  avgAmount: number;
}

export const useCashOutflowReport = (filters: CashOutflowFilters) => {
  const displayCurrency = filters.displayCurrency || 'ETB';
  const { user } = useAuth();
  const { data: paymentRequests, isLoading: prLoading } = usePaymentRequests();
  const { data: expenses, isLoading: expLoading } = useExpenses({
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
  });
  const { data: departments } = useDepartments();
  const { data: categories } = useExpenseCategories();
  const { rates, isLoading: ratesLoading } = useExchangeRates();

  // Fetch internal transfers for the date range
  const { data: internalTransfers } = useQuery({
    queryKey: ['cash-outflow-internal-transfers', filters.dateFrom, filters.dateTo],
    queryFn: async () => {
      let query = supabase
        .from('internal_transfers' as any)
        .select('*');
      if (filters.dateFrom) query = query.gte('transfer_date', filters.dateFrom);
      if (filters.dateTo) query = query.lte('transfer_date', filters.dateTo);
      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!user,
  });

  // Build a converter function that converts any amount from its source currency to displayCurrency
  const convertAmount = useMemo(() => {
    // Build a map of currency_pair -> sorted rates by effective_date desc
    const rateMap = new Map<string, { rate: number; effective_date: string }[]>();
    for (const r of rates) {
      const list = rateMap.get(r.currency_pair) || [];
      list.push({ rate: r.rate, effective_date: r.effective_date });
      rateMap.set(r.currency_pair, list);
    }
    // Sort each list desc
    for (const [, list] of rateMap) {
      list.sort((a, b) => b.effective_date.localeCompare(a.effective_date));
    }

    const getRate = (pair: string, date?: string): number | null => {
      const list = rateMap.get(pair);
      if (!list || list.length === 0) return null;
      if (!date) return list[0].rate; // latest rate
      for (const r of list) {
        if (r.effective_date <= date) return r.rate;
      }
      return list[list.length - 1].rate; // oldest available
    };

    return (amount: number, sourceCurrency: string, date?: string): number => {
      const src = (sourceCurrency || 'ETB').toUpperCase();
      const tgt = displayCurrency.toUpperCase();
      if (src === tgt) return amount;

      // Try direct pair: src/tgt (e.g., USD/ETB rate = how many ETB per 1 USD)
      const directRate = getRate(`${src}/${tgt}`, date);
      if (directRate != null) return amount * directRate;

      // Try inverse pair: tgt/src
      const inverseRate = getRate(`${tgt}/${src}`, date);
      if (inverseRate != null && inverseRate !== 0) return amount / inverseRate;

      // Try cross through ETB
      if (src !== 'ETB' && tgt !== 'ETB') {
        // Convert src -> ETB -> tgt
        const srcToEtb = getRate(`${src}/ETB`, date);
        const tgtToEtb = getRate(`${tgt}/ETB`, date);
        if (srcToEtb != null && tgtToEtb != null && tgtToEtb !== 0) {
          return (amount * srcToEtb) / tgtToEtb;
        }
      }

      // No rate found - return as-is (will be in original currency)
      return amount;
    };
  }, [rates, displayCurrency]);

  const filteredPaymentRequests = useMemo(() => {
    if (!paymentRequests) return [];
    return paymentRequests.filter(pr => {
      if (!filters.dateFrom && !filters.dateTo) return true;
      const prDate = pr.submitted_at || pr.created_at;
      if (filters.dateFrom && prDate < filters.dateFrom) return false;
      if (filters.dateTo && prDate > filters.dateTo) return false;
      return true;
    });
  }, [paymentRequests, filters.dateFrom, filters.dateTo]);

  // Helper to get converted expense amount
  const expAmt = (exp: { net_payment?: number | null; total_amount: number; currency: string; expense_date: string; payment_status: string }) => {
    const raw = (exp.payment_status === 'paid' && exp.net_payment) ? exp.net_payment : exp.total_amount;
    return convertAmount(raw, exp.currency || 'ETB', exp.expense_date);
  };

  // Helper to get converted PR amount
  const prAmt = (pr: any) => {
    const raw = pr.expense?.[0]?.net_payment ?? pr.total_amount;
    const date = pr.paid_at || pr.submitted_at || pr.created_at;
    return convertAmount(raw, pr.currency || 'ETB', date);
  };

  const kpis = useMemo((): CashOutflowKPIs => {
    const paidExpenses = expenses?.filter(e => e.payment_status === 'paid') || [];
    const paidPRs = filteredPaymentRequests.filter(pr => pr.status === 'paid');
    const approvedPRs = filteredPaymentRequests.filter(pr => pr.status === 'approved');
    const rejectedPRs = filteredPaymentRequests.filter(pr => pr.status === 'rejected');
    const decidedPRs = [...paidPRs, ...approvedPRs, ...rejectedPRs];

    const totalExpenses = paidExpenses.reduce((sum, e) => sum + expAmt(e), 0);
    const totalPaidPRs = paidPRs.reduce((sum, pr) => sum + prAmt(pr), 0);
    const totalCashOutflow = totalExpenses + totalPaidPRs;

    const pendingOutflow = approvedPRs.reduce((sum, pr) => sum + convertAmount(pr.total_amount, pr.currency || 'ETB', pr.submitted_at || pr.created_at), 0);

    const prsWithDates = paidPRs.filter(pr => pr.submitted_at && pr.paid_at);
    const avgProcessingDays = prsWithDates.length > 0
      ? prsWithDates.reduce((sum, pr) => {
          return sum + differenceInDays(parseISO(pr.paid_at!), parseISO(pr.submitted_at!));
        }, 0) / prsWithDates.length
      : 0;

    const approvalRate = decidedPRs.length > 0
      ? ((paidPRs.length + approvedPRs.length) / decidedPRs.length) * 100
      : 0;

    const now = new Date();
    const threeMonthsAgo = subMonths(now, 3);
    const recentExpenses = paidExpenses.filter(e => parseISO(e.expense_date) >= threeMonthsAgo);
    const recentPRs = paidPRs.filter(pr => pr.paid_at && parseISO(pr.paid_at) >= threeMonthsAgo);
    const recentTotal = recentExpenses.reduce((sum, e) => sum + expAmt(e), 0) +
                        recentPRs.reduce((sum, pr) => sum + prAmt(pr), 0);
    const monthlyBurnRate = recentTotal / 3;

    // Calculate excluded internal transfers total
    const transfersTotal = (internalTransfers || []).reduce((sum: number, t: any) => {
      return sum + convertAmount(Number(t.from_amount) || 0, t.from_currency || 'ETB', t.transfer_date);
    }, 0);

    return {
      totalCashOutflow,
      pendingOutflow,
      avgProcessingDays: Math.round(avgProcessingDays * 10) / 10,
      approvalRate: Math.round(approvalRate * 10) / 10,
      monthlyBurnRate,
      totalTransactions: paidExpenses.length + paidPRs.length,
      rejectedCount: rejectedPRs.length,
      paidCount: paidPRs.length,
      internalTransfersExcluded: transfersTotal,
      internalTransfersCount: (internalTransfers || []).length,
    };
  }, [expenses, filteredPaymentRequests, convertAmount, internalTransfers]);

  const timeSeriesData = useMemo((): TimeSeriesDataPoint[] => {
    const paidExpenses = expenses?.filter(e => e.payment_status === 'paid') || [];
    const paidPRs = filteredPaymentRequests.filter(pr => pr.status === 'paid');

    const dataMap = new Map<string, TimeSeriesDataPoint>();

    const getKey = (dateStr: string) => {
      const date = parseISO(dateStr);
      switch (filters.aggregationPeriod) {
        case 'daily':
          return format(date, 'yyyy-MM-dd');
        case 'weekly':
          return format(date, "yyyy-'W'ww");
        case 'monthly':
          return format(date, 'yyyy-MM');
        case 'quarterly':
          const quarter = Math.ceil((date.getMonth() + 1) / 3);
          return `${date.getFullYear()}-Q${quarter}`;
        default:
          return format(date, 'yyyy-MM');
      }
    };

    paidExpenses.forEach(exp => {
      const key = getKey(exp.expense_date);
      const existing = dataMap.get(key) || { period: key, expenses: 0, paymentRequests: 0, total: 0 };
      const amount = expAmt(exp);
      existing.expenses += amount;
      existing.total += amount;
      dataMap.set(key, existing);
    });

    paidPRs.forEach(pr => {
      if (pr.paid_at) {
        const key = getKey(pr.paid_at);
        const existing = dataMap.get(key) || { period: key, expenses: 0, paymentRequests: 0, total: 0 };
        const amount = prAmt(pr);
        existing.paymentRequests += amount;
        existing.total += amount;
        dataMap.set(key, existing);
      }
    });

    return Array.from(dataMap.values()).sort((a, b) => a.period.localeCompare(b.period));
  }, [expenses, filteredPaymentRequests, filters.aggregationPeriod, convertAmount]);

  const categoryBreakdown = useMemo((): CategoryBreakdown[] => {
    const paidExpenses = expenses?.filter(e => e.payment_status === 'paid') || [];
    const categoryMap = new Map<string, { value: number; count: number }>();

    paidExpenses.forEach(exp => {
      const catName = exp.category?.name || 'Uncategorized';
      const existing = categoryMap.get(catName) || { value: 0, count: 0 };
      existing.value += expAmt(exp);
      existing.count += 1;
      categoryMap.set(catName, existing);
    });

    return Array.from(categoryMap.entries())
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.value - a.value);
  }, [expenses, convertAmount]);

  const expenseCategoryBreakdown = useMemo((): ExpenseCategoryBreakdown[] => {
    const paidExpenses = expenses?.filter(e => e.payment_status === 'paid') || [];
    if (!categories || categories.length === 0) return [];

    const expenseCategories = categories.filter(c => c.category_type === 'expense_category');
    const costGroups = categories.filter(c => c.category_type === 'cost_group');
    const costPositions = categories.filter(c => c.category_type === 'cost_position');

    const result: ExpenseCategoryBreakdown[] = [];

    expenseCategories.forEach(expCat => {
      const catCostGroups = costGroups.filter(cg => cg.parent_id === expCat.id);
      const costGroupBreakdowns: CostGroupBreakdown[] = [];
      let categoryTotal = 0;
      let categoryCount = 0;

      catCostGroups.forEach(group => {
        const groupPositions = costPositions.filter(cp => cp.parent_id === group.id);
        const positionBreakdowns: CostPositionBreakdown[] = [];
        let groupTotal = 0;
        let groupCount = 0;

        groupPositions.forEach(position => {
          const positionExpenses = paidExpenses.filter(exp => exp.category_id === position.id);
          const positionValue = positionExpenses.reduce((sum, exp) => sum + expAmt(exp), 0);
          const positionCount = positionExpenses.length;

          if (positionValue > 0 || positionCount > 0) {
            positionBreakdowns.push({
              id: position.id,
              name: position.name,
              code: position.code,
              value: positionValue,
              count: positionCount,
            });
          }

          groupTotal += positionValue;
          groupCount += positionCount;
        });

        const directGroupExpenses = paidExpenses.filter(exp => exp.category_id === group.id);
        const directGroupValue = directGroupExpenses.reduce((sum, exp) => sum + expAmt(exp), 0);
        const directGroupCount = directGroupExpenses.length;

        if (directGroupValue > 0) {
          positionBreakdowns.push({
            id: group.id + '-direct',
            name: 'Other ' + group.name,
            code: group.code + '-OTH',
            value: directGroupValue,
            count: directGroupCount,
          });
          groupTotal += directGroupValue;
          groupCount += directGroupCount;
        }

        if (groupTotal > 0 || positionBreakdowns.length > 0) {
          costGroupBreakdowns.push({
            id: group.id,
            name: group.name,
            code: group.code,
            value: groupTotal,
            count: groupCount,
            costPositions: positionBreakdowns.sort((a, b) => b.value - a.value),
          });
        }

        categoryTotal += groupTotal;
        categoryCount += groupCount;
      });

      const directCatExpenses = paidExpenses.filter(exp => exp.category_id === expCat.id);
      const directCatValue = directCatExpenses.reduce((sum, exp) => sum + expAmt(exp), 0);
      const directCatCount = directCatExpenses.length;

      if (directCatValue > 0) {
        costGroupBreakdowns.push({
          id: expCat.id + '-direct',
          name: 'Other ' + expCat.name,
          code: expCat.code + '-OTH',
          value: directCatValue,
          count: directCatCount,
          costPositions: [],
        });
        categoryTotal += directCatValue;
        categoryCount += directCatCount;
      }

      if (categoryTotal > 0 || costGroupBreakdowns.length > 0) {
        result.push({
          id: expCat.id,
          name: expCat.name,
          code: expCat.code,
          value: categoryTotal,
          count: categoryCount,
          costGroups: costGroupBreakdowns.sort((a, b) => b.value - a.value),
        });
      }
    });

    return result.sort((a, b) => b.value - a.value);
  }, [expenses, categories, convertAmount]);

  const costGroupBreakdown = useMemo((): CostGroupBreakdown[] => {
    return expenseCategoryBreakdown.flatMap(cat => cat.costGroups);
  }, [expenseCategoryBreakdown]);

  const departmentBreakdown = useMemo((): DepartmentBreakdown[] => {
    const paidExpenses = expenses?.filter(e => e.payment_status === 'paid') || [];
    const paidPRs = filteredPaymentRequests.filter(pr => pr.status === 'paid');
    const deptMap = new Map<string, { value: number; count: number }>();

    paidExpenses.forEach(exp => {
      const deptName = exp.department?.name || 'Unassigned';
      const existing = deptMap.get(deptName) || { value: 0, count: 0 };
      existing.value += expAmt(exp);
      existing.count += 1;
      deptMap.set(deptName, existing);
    });

    paidPRs.forEach(pr => {
      const deptName = pr.department?.name || 'Unassigned';
      const existing = deptMap.get(deptName) || { value: 0, count: 0 };
      existing.value += prAmt(pr);
      existing.count += 1;
      deptMap.set(deptName, existing);
    });

    return Array.from(deptMap.entries())
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.value - a.value);
  }, [expenses, filteredPaymentRequests, convertAmount]);

  const paymentMethodBreakdown = useMemo((): PaymentMethodBreakdown[] => {
    const paidPRs = filteredPaymentRequests.filter(pr => pr.status === 'paid');
    const methodMap = new Map<string, { value: number; count: number }>();

    paidPRs.forEach(pr => {
      const method = pr.payment_method || 'Unknown';
      const existing = methodMap.get(method) || { value: 0, count: 0 };
      existing.value += prAmt(pr);
      existing.count += 1;
      methodMap.set(method, existing);
    });

    return Array.from(methodMap.entries())
      .map(([method, data]) => ({ method, ...data }))
      .sort((a, b) => b.value - a.value);
  }, [filteredPaymentRequests, convertAmount]);

  const statusFunnel = useMemo((): StatusFunnel[] => {
    const statusOrder = ['draft', 'pending_approval', 'approved', 'paid', 'rejected', 'cancelled'];
    const statusMap = new Map<string, { count: number; value: number }>();

    filteredPaymentRequests.forEach(pr => {
      const existing = statusMap.get(pr.status) || { count: 0, value: 0 };
      existing.count += 1;
      existing.value += convertAmount(pr.total_amount, pr.currency || 'ETB', pr.submitted_at || pr.created_at);
      statusMap.set(pr.status, existing);
    });

    return statusOrder
      .filter(status => statusMap.has(status))
      .map(status => ({
        status: status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
        ...statusMap.get(status)!,
      }));
  }, [filteredPaymentRequests, convertAmount]);

  const processingTimeData = useMemo((): ProcessingTimeData[] => {
    const paidPRs = filteredPaymentRequests.filter(pr => 
      pr.status === 'paid' && pr.submitted_at && pr.approved_at && pr.paid_at
    );

    const deptMap = new Map<string, { toApprove: number[]; toPay: number[]; total: number[] }>();

    paidPRs.forEach(pr => {
      const deptName = pr.department?.name || 'Unassigned';
      const existing = deptMap.get(deptName) || { toApprove: [], toPay: [], total: [] };
      
      const daysToApprove = differenceInDays(parseISO(pr.approved_at!), parseISO(pr.submitted_at!));
      const daysToPay = differenceInDays(parseISO(pr.paid_at!), parseISO(pr.approved_at!));
      const totalDays = differenceInDays(parseISO(pr.paid_at!), parseISO(pr.submitted_at!));

      existing.toApprove.push(daysToApprove);
      existing.toPay.push(daysToPay);
      existing.total.push(totalDays);
      deptMap.set(deptName, existing);
    });

    const avg = (arr: number[]) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

    return Array.from(deptMap.entries()).map(([department, data]) => ({
      department,
      avgDaysToApprove: Math.round(avg(data.toApprove) * 10) / 10,
      avgDaysToPay: Math.round(avg(data.toPay) * 10) / 10,
      avgTotalDays: Math.round(avg(data.total) * 10) / 10,
    }));
  }, [filteredPaymentRequests]);

  const vendorAnalysis = useMemo((): VendorAnalysis[] => {
    const paidPRs = filteredPaymentRequests.filter(pr => pr.status === 'paid' && pr.vendor_name);
    const vendorMap = new Map<string, { total: number; count: number }>();

    paidPRs.forEach(pr => {
      const vendor = pr.vendor_name!;
      const existing = vendorMap.get(vendor) || { total: 0, count: 0 };
      existing.total += prAmt(pr);
      existing.count += 1;
      vendorMap.set(vendor, existing);
    });

    return Array.from(vendorMap.entries())
      .map(([vendorName, data]) => ({
        vendorName,
        totalAmount: data.total,
        transactionCount: data.count,
        avgAmount: data.total / data.count,
      }))
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, 10);
  }, [filteredPaymentRequests, convertAmount]);

  return {
    isLoading: prLoading || expLoading || ratesLoading,
    kpis,
    timeSeriesData,
    categoryBreakdown,
    expenseCategoryBreakdown,
    costGroupBreakdown,
    departmentBreakdown,
    paymentMethodBreakdown,
    statusFunnel,
    processingTimeData,
    vendorAnalysis,
    paymentRequests: filteredPaymentRequests,
    expenses: expenses || [],
    displayCurrency,
    convertAmount,
  };
};
